/**
 * Circulation Statistics Page - Detailed circulation metrics with charts.
 * Shows loan activity, overdue analysis, trends, and performance indicators.
 */

import { useQuery } from "@tanstack/react-query";
import { get } from "@/api/client";
import {
  ArrowLeft,
  Clock,
  BookOpen,
  Users,
  Calendar,
  AlertTriangle,
  RefreshCw,
  CheckCircle2,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  DonutChart,
  SimpleBarChart,
  TrendChart,
  Gauge,
  StatTile,
  ProgressBar,
} from "../components";

interface DashboardCirculationData {
  circulation: {
    activeLoans: number;
    overdueLoans: number;
    loansToday: number;
    returnsToday: number;
    reservationsPending: number;
  };
  patrons: {
    totalPatrons: number;
    activePatrons: number;
    newPatronsThisMonth: number;
  };
  generatedAt: string;
}

export function CirculationStatsPage() {
  const { data, isLoading, error } = useQuery<DashboardCirculationData>({
    queryKey: ["statistics", "circulation"],
    queryFn: () => get<DashboardCirculationData>("/statistics/dashboard"),
    refetchInterval: 60000,
  });

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center dark:border-red-800 dark:bg-red-900/20">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
          <p className="mt-4 font-medium text-red-800 dark:text-red-200">
            Erreur lors du chargement des statistiques
          </p>
        </div>
      </div>
    );
  }

  const overdueRate =
    data.circulation.activeLoans > 0
      ? (data.circulation.overdueLoans / data.circulation.activeLoans) * 100
      : 0;

  const activityRate =
    data.patrons.totalPatrons > 0
      ? (data.patrons.activePatrons / data.patrons.totalPatrons) * 100
      : 0;

  // Chart data
  const loanStatusData = [
    {
      name: "À jour",
      value: data.circulation.activeLoans - data.circulation.overdueLoans,
      color: "#22c55e",
    },
    { name: "En retard", value: data.circulation.overdueLoans, color: "#ef4444" },
  ];

  // Mock weekly trend data
  const weeklyTrendData = [
    { name: "Lun", loans: 45, returns: 38 },
    { name: "Mar", loans: 52, returns: 48 },
    { name: "Mer", loans: 38, returns: 42 },
    { name: "Jeu", loans: 61, returns: 55 },
    { name: "Ven", loans: 55, returns: 62 },
    { name: "Sam", loans: 78, returns: 70 },
    { name: "Dim", loans: 12, returns: 15 },
  ];

  // Mock hourly distribution
  const hourlyData = [
    { name: "9h", value: 12 },
    { name: "10h", value: 28 },
    { name: "11h", value: 35 },
    { name: "12h", value: 18 },
    { name: "14h", value: 42 },
    { name: "15h", value: 55 },
    { name: "16h", value: 48 },
    { name: "17h", value: 32 },
    { name: "18h", value: 15 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/statistics"
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Statistiques de circulation
            </h1>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              Analyse détaillée des prêts et retours
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-400">
          <Clock className="h-4 w-4" />
          {new Date(data.generatedAt).toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Prêts en cours"
          value={data.circulation.activeLoans}
          icon={<BookOpen className="h-5 w-5" />}
          color="blue"
          sparklineData={[450, 465, 480, 475, 490, 485, 510, 505, 520, 530]}
        />
        <StatTile
          label="Prêts aujourd'hui"
          value={data.circulation.loansToday}
          change={12}
          changeLabel="vs hier"
          icon={<ArrowUpRight className="h-5 w-5" />}
          color="green"
        />
        <StatTile
          label="Retours aujourd'hui"
          value={data.circulation.returnsToday}
          icon={<ArrowDownRight className="h-5 w-5" />}
          color="purple"
        />
        <StatTile
          label="Réservations"
          value={data.circulation.reservationsPending}
          icon={<Calendar className="h-5 w-5" />}
          color="amber"
        />
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Weekly Trend */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 lg:col-span-2 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Activité de la semaine
          </h2>
          <TrendChart
            data={weeklyTrendData.map((d) => ({
              name: d.name,
              value: d.loans,
              returns: d.returns,
            }))}
            height={280}
            dataKeys={[
              { key: "value", color: "#3b82f6", name: "Prêts" },
              { key: "returns", color: "#22c55e", name: "Retours" },
            ]}
          />
        </div>

        {/* Loan Status Donut */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            État des prêts
          </h2>
          <DonutChart
            data={loanStatusData}
            centerValue={data.circulation.activeLoans}
            centerLabel="prêts actifs"
            size={180}
          />
        </div>
      </div>

      {/* Overdue Analysis */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-4 flex items-center gap-2">
            <AlertTriangle
              className={`h-5 w-5 ${overdueRate > 10 ? "text-red-500" : "text-amber-500"}`}
            />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Analyse des retards
            </h2>
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <div className="flex flex-col items-center justify-center">
              <Gauge value={overdueRate} size={140} thresholds={{ warning: 5, danger: 10 }} />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Taux de retard</p>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">En retard</span>
                  <span className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {data.circulation.overdueLoans}
                  </span>
                </div>
              </div>
              <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">À jour</span>
                  <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {data.circulation.activeLoans - data.circulation.overdueLoans}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {overdueRate > 5 && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
              <h3 className="font-medium text-amber-800 dark:text-amber-200">Recommandations</h3>
              <ul className="mt-2 space-y-1 text-sm text-amber-700 dark:text-amber-300">
                {overdueRate > 10 && <li>• Envoyer des rappels automatiques</li>}
                <li>• Contacter les lecteurs concernés</li>
                <li>• Réviser les durées de prêt si nécessaire</li>
              </ul>
            </div>
          )}
        </div>

        {/* Hourly Distribution */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Répartition horaire
          </h2>
          <SimpleBarChart data={hourlyData} height={250} />
          <p className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
            Nombre de transactions par heure d'ouverture
          </p>
        </div>
      </div>

      {/* Patron Activity Section */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-6 flex items-center gap-2">
          <Users className="h-5 w-5 text-purple-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Activité des lecteurs
          </h2>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Stats */}
          <div className="grid gap-4 sm:grid-cols-3 lg:col-span-2 lg:grid-cols-3">
            <div className="rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-5 text-white">
              <div className="text-3xl font-bold">
                {data.patrons.totalPatrons.toLocaleString("fr-FR")}
              </div>
              <div className="mt-1 text-purple-100">Inscrits</div>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-5 text-white">
              <div className="text-3xl font-bold">
                {data.patrons.activePatrons.toLocaleString("fr-FR")}
              </div>
              <div className="mt-1 text-blue-100">Actifs</div>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-green-500 to-green-600 p-5 text-white">
              <div className="text-3xl font-bold">+{data.patrons.newPatronsThisMonth}</div>
              <div className="mt-1 text-green-100">Ce mois</div>
            </div>
          </div>

          {/* Activity Rate */}
          <div className="flex flex-col justify-center">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Taux d'activité
              </span>
              <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
                {activityRate.toFixed(1)}%
              </span>
            </div>
            <ProgressBar
              value={data.patrons.activePatrons}
              max={data.patrons.totalPatrons}
              color="#8b5cf6"
              showLabel={false}
              size="lg"
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {data.patrons.activePatrons.toLocaleString("fr-FR")} lecteurs actifs sur{" "}
              {data.patrons.totalPatrons.toLocaleString("fr-FR")} inscrits
            </p>
          </div>
        </div>
      </div>

      {/* Today's Summary */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-blue-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Résumé du jour</h2>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex items-center gap-4 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/50">
              <ArrowUpRight className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {data.circulation.loansToday}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Prêts</div>
            </div>
          </div>

          <div className="flex items-center gap-4 rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50">
              <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {data.circulation.returnsToday}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Retours</div>
            </div>
          </div>

          <div className="flex items-center gap-4 rounded-lg bg-purple-50 p-4 dark:bg-purple-900/20">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/50">
              <RefreshCw className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {data.circulation.loansToday + data.circulation.returnsToday}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Transactions</div>
            </div>
          </div>

          <div className="flex items-center gap-4 rounded-lg bg-amber-50 p-4 dark:bg-amber-900/20">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/50">
              <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {data.circulation.reservationsPending}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">En attente</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Link
          to="/circulation"
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
        >
          <RefreshCw className="h-4 w-4" />
          Gérer les prêts
        </Link>
        <Link
          to="/circulation/patrons"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <Users className="h-4 w-4" />
          Gérer les lecteurs
        </Link>
        <Link
          to="/statistics"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <TrendingUp className="h-4 w-4" />
          Vue d'ensemble
        </Link>
      </div>
    </div>
  );
}
